import { useMemo } from 'react'
import styled from 'styled-components'
import { LayoutGroup } from 'framer-motion'
import { HealthPlanCard } from './HealthPlanCard'
import { HealthPlanMobileCard } from './HealthPlanMobileCard'
import { HealthPlanCardResponse } from '../../../api/ester-b2b-api/private/healthPlan/types'
import { useWindowWidth } from '../../../hooks/useWindowWidth'
import { theme } from '../../../theme/theme'
import { isMobile as isMobileDevice } from '../../../utils/devices'

type Props = {
  cards: HealthPlanCardResponse[]
  onCardClick: (card: HealthPlanCardResponse) => void
  expandedCard?: HealthPlanCardResponse | null
  onClose?: () => void
  scrollTargetRef?: React.RefObject<HTMLDivElement>
}

export const HealthPlanCardList = ({ cards, onCardClick, expandedCard, onClose, scrollTargetRef }: Props) => {
  const windowWidth = useWindowWidth()
  const isMobile = isMobileDevice(windowWidth)

  const handleCardClick = (card: HealthPlanCardResponse) => {
    onCardClick(card)

    if (!isMobile && scrollTargetRef?.current) {
      scrollTargetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const reorderedCards = useMemo(() => {
    if (!expandedCard || isMobile) return cards

    const expanded = cards.find(({ module }) => module === expandedCard.module)
    if (!expanded) return cards

    const others = cards.filter(({ module }) => module !== expandedCard.module)

    return [expanded, ...others]
  }, [cards, expandedCard, isMobile])

  return (
    <>
      <LayoutGroup>
        <CardGrid>
          {reorderedCards.map((card) => {
            const isExpanded = !isMobile && expandedCard?.module === card.module

            return (
              <HealthPlanCard
                key={card.module}
                card={card}
                onCardClick={handleCardClick}
                isExpanded={isExpanded}
                onClose={onClose}
              />
            )
          })}
        </CardGrid>
      </LayoutGroup>

      <HealthPlanMobileCard
        card={expandedCard || null}
        isOpen={isMobile && Boolean(expandedCard)}
        onClose={() => onClose?.()}
      />
    </>
  )
}

const CardGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.mediumLarge}px;

  @media (max-width: ${theme.breakpoint.medium}px) {
    flex-direction: column;
    gap: ${theme.spacing.medium}px;
  }
`
